import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Account } from '../types';
import { accountsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import AccountHistory from '../components/AccountHistory';
import AccountValue from '../components/AccountValue';
import { useAuth } from '../contexts/AuthContext';
import { useAccountFilter } from '../contexts/AccountFilterContext';
import { convertCurrency } from '../services/currencyService';
import AddLiabilityModal from '../components/AddLiabilityModal';

const Liabilities: React.FC = () => {
  const { user } = useAuth();
  const { accountFilter } = useAccountFilter();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [viewingHistory, setViewingHistory] = useState<Account | null>(null);
  const historyScrollRef = useRef<number>(0);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await accountsAPI.getAccounts({ type: 'liability' });
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const openModal = () => {
    setEditingAccount(null);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      const scrollY = window.scrollY;
      try {
        await accountsAPI.deleteAccount(id);
        await fetchAccounts();
        setTimeout(() => {
          window.scrollTo(0, scrollY);
        }, 0);
      } catch (error) {
        console.error('Error deleting account:', error);
      }
    }
  };

  // Filter accounts based on accountFilter
  const filteredAccounts = useMemo(() => {
    if (accountFilter === 'all') return accounts;
    return accounts.filter(account => account.account_type === accountFilter);
  }, [accounts, accountFilter]);

  // Calculate total value with currency conversion
  const [totalValue, setTotalValue] = useState<number>(0);
  const [totalValueLoading, setTotalValueLoading] = useState<boolean>(false);

  useEffect(() => {
    const calculateTotalValue = async () => {
      try {
        setTotalValueLoading(true);
        let total = 0;
        
        if (filteredAccounts.length === 0) {
          setTotalValue(0);
          setTotalValueLoading(false);
          return;
        }

        const userPreferredCurrency = user?.default_currency || 'CAD';

        for (const account of filteredAccounts) {
          const value = parseFloat(account.current_value?.toString() || '0') || 0;
          const accountCurrency = account.currency || 'CAD';
          
          if (accountCurrency === userPreferredCurrency) {
            total += value;
          } else {
            try {
              const convertedValue = await convertCurrency(value, accountCurrency, userPreferredCurrency);
              total += convertedValue;
            } catch (conversionError) {
              console.error('Currency conversion failed for account:', account.id, conversionError);
              total += value;
            }
          }
        }
        
        setTotalValue(total);
      } catch (error) {
        console.error('Error calculating total value:', error);
        const fallbackTotal = filteredAccounts.reduce((sum: number, account: Account) => {
          const value = parseFloat(account.current_value?.toString() || '0') || 0;
          return sum + value;
        }, 0);
        setTotalValue(fallbackTotal);
      } finally {
        setTotalValueLoading(false);
      }
    };

    calculateTotalValue();
  }, [filteredAccounts, user?.default_currency]);

  if (loading) {
    return (
      <div className="loading-container dark-theme">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading your liabilities...</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="card">
        <div className="card-header">
          <div>
            <h1 className="card-title">💳 Liabilities</h1>
            <p className="card-subtitle">Manage your debts and obligations</p>
          </div>
          <div className="summary-value">
            Total: {totalValueLoading ? 'Calculating...' : 
                   isNaN(totalValue) ? 'Error' : 
                   formatCurrency(totalValue, user?.default_currency || 'CAD')}
          </div>
        </div>

        {filteredAccounts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <h3 className="empty-state-title">No liabilities yet</h3>
            <p className="empty-state-description">
              Start tracking your debts by adding your first liability.
            </p>
            <button onClick={openModal} className="btn btn-primary">
              ➕ Add Liability
            </button>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Institution</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Currency</th>
                    <th>Current Balance</th>
                    <th>Last Updated</th>
                    <th>Linked To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <tr key={account.id}>
                      <td>{account.institution}</td>
                      <td>{account.name}</td>
                      <td>{account.category}</td>
                      <td>
                        <span className={`badge ${account.account_type}`}>
                          {account.account_type}
                        </span>
                      </td>
                      <td>
                        <span className="currency-badge">
                          {account.currency}
                        </span>
                      </td>
                      <td>
                        <AccountValue
                          value={account.current_value || 0}
                          currency={account.currency}
                          userPreferredCurrency={user?.default_currency || 'CAD'}
                          showOriginal={account.currency !== (user?.default_currency || 'CAD')}
                        />
                      </td>
                      <td>
                        {account.latest_snapshot_date 
                          ? formatDate(account.latest_snapshot_date)
                          : 'Never'
                        }
                      </td>
                      <td>
                        {account.linked_account_id ? (
                          <div className="linked-account-info">
                            <div className="linked-account-header">
                              {account.plaid_logo_url && (
                                <img 
                                  src={account.plaid_logo_url} 
                                  alt={account.plaid_institution_name || 'Bank'}
                                  className="linked-account-logo"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              )}
                              <span className="linked-account-name">
                                {account.plaid_account_name || 'Plaid Account'}
                              </span>
                            </div>
                            <div className="linked-account-details">
                              <span className="linked-account-mask">
                                •••• {account.plaid_account_mask}
                              </span>
                              <span className="linked-account-type">
                                {account.plaid_account_subtype}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <span className="not-linked">Not linked</span>
                        )}
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            onClick={() => { historyScrollRef.current = window.scrollY; setViewingHistory(account); }}
                            className="btn btn-secondary btn-sm"
                          >
                            📊 History
                          </button>
                          <button
                            onClick={() => handleEdit(account)}
                            className="btn btn-secondary btn-sm"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDelete(account.id)}
                            className="btn btn-danger btn-sm"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <button onClick={openModal} className="btn btn-primary">
                ➕ Add Liability
              </button>
            </div>
          </>
        )}
      </div>

      <AddLiabilityModal
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) setEditingAccount(null);
        }}
        editingAccount={editingAccount}
        onSuccess={async () => {
          const scrollY = window.scrollY;
          await fetchAccounts();
          setTimeout(() => window.scrollTo(0, scrollY), 0);
        }}
        existingAccounts={accounts}
      />

      {viewingHistory && (
        <AccountHistory
          account={viewingHistory}
          onClose={() => {
            setViewingHistory(null);
            const savedScroll = historyScrollRef.current;
            fetchAccounts().then(() => {
              requestAnimationFrame(() => window.scrollTo(0, savedScroll));
            });
          }}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default Liabilities; 